import React, { useMemo } from 'react';

import { useIntl } from '@futrli/localisation';

import { ListingPageButtonToggle } from '@futrli/ui-components/components/listing-page/listing-page-button-toggle/listing-page-button-toggle';
import { StatusFilterValue, TypeFilterValue } from './use-status-orientation-filter';

interface StatusOrientationFiltersProps {
  statusFilter: StatusFilterValue;
  onStatusChange: (value: StatusFilterValue) => void;
  typeFilter: TypeFilterValue;
  onTypeChange: (value: TypeFilterValue) => void;
  isResourceSharingEnabled?: boolean;
}

/**
 * Shared filter toggle controls for My Reports and Report Templates tabs.
 * Renders a Status or Sharing toggle and a Type shown toggle (All / Portrait / Landscape).
 * Designed to be placed as children inside ReportFilterHeader.
 */
export const StatusOrientationFilters = ({
  statusFilter,
  onStatusChange,
  typeFilter,
  onTypeChange,
  isResourceSharingEnabled = false
}: StatusOrientationFiltersProps) => {
  const intl = useIntl();

  const statusOptions = useMemo(
    () =>
      isResourceSharingEnabled
        ? [
          { label: intl.formatMessage({ id: 'predict.all', defaultMessage: 'All' }), value: 'all' },
          { label: intl.formatMessage({ id: 'common.shared', defaultMessage: 'Shared' }), value: 'shared' },
          { label: intl.formatMessage({ id: 'common.private', defaultMessage: 'Private' }), value: 'private' }
        ]
        : [
          { label: intl.formatMessage({ id: 'predict.all', defaultMessage: 'All' }), value: 'all' },
          { label: intl.formatMessage({ id: 'common.published', defaultMessage: 'Published' }), value: 'published' },
          { label: intl.formatMessage({ id: 'common.draft', defaultMessage: 'Draft' }), value: 'draft' }
        ],
    [intl, isResourceSharingEnabled]
  );
  const statusLabel = isResourceSharingEnabled
    ? intl.formatMessage({ id: 'common.sharing', defaultMessage: 'Sharing' })
    : intl.formatMessage({ id: 'common.status', defaultMessage: 'Status' });

  const typeOptions = useMemo(
    () => [
      { label: intl.formatMessage({ id: 'predict.all', defaultMessage: 'All' }), value: 'all' },
      { label: intl.formatMessage({ id: 'reporting.portrait', defaultMessage: 'Portrait' }), value: 'portrait' },
      { label: intl.formatMessage({ id: 'reporting.landscape', defaultMessage: 'Landscape' }), value: 'landscape' }
    ],
    [intl]
  );

  return (
    <>
      <ListingPageButtonToggle
        label={statusLabel}
        options={statusOptions}
        selectedValue={statusFilter}
        onChange={value => onStatusChange(value as StatusFilterValue)}
      />
      <ListingPageButtonToggle
        label={intl.formatMessage({ id: 'predict.type_shown', defaultMessage: 'Type shown' })}
        options={typeOptions}
        selectedValue={typeFilter}
        onChange={value => onTypeChange(value as TypeFilterValue)}
      />
    </>
  );
};
